import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, reactive, toRefs, watch } from 'vue';
import type { IMatch } from '@/interface/IMatch';
import type { IOdds, IPriceOption } from '@/interface/IOdds';
import { MarketType, OddsOption, OddsStatus, SportType } from '@/interface/enum';
import { isCorrectScoreMarket, isFormatPoint, isShowPointGroup, isShowPointMarket, isShowPointSport } from '@/libraries/oddsDisplay';
import { toFixedDecimalPoint } from '@/libraries/utils/text';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
import { useSingleBetSlipStore } from '@/stores/betSlip/singleBetSlipStore';
import { useRootStore } from '@/stores/rootStore';

export default function useOdds(matchInfo: Ref<IMatch>, oddsInfo: Ref<IOdds>, priceOption: Ref<IPriceOption>) {
    const { isTicketExist } = useSingleBetSlipStore();
    const { isMixParlayMode } = storeToRefs(useRootStore());
    const { isTicketExist: isMixParlayTicketExist } = useParlayBetSlipStore();
    const { isShowSingleBetSlip, isShowParlayBetSlip } = storeToRefs(useBetSlipStore());

    const oddsStatus = reactive({
        oddRise: false,
        oddDrop: false,
        isOddsNegative: computed(() => priceOption.value.price < 0),
        /** Double Chance: a price of 0 means that selection is closed; other selections may still be offered. */
        isOddsSuspended: computed(
            () => oddsInfo.value.status === OddsStatus.Suspend
            || (oddsInfo.value.marketType === MarketType.DoubleChance && priceOption.value.price === 0),
        ),
        isOddsSelected: computed(() => {
            const key = `${oddsInfo.value.oddsId}-${priceOption.value.option}`;
            return isMixParlayMode.value
                ? isShowParlayBetSlip.value && isMixParlayTicketExist(key)
                : isShowSingleBetSlip.value && isTicketExist(key);
        }),
    });

    const point = computed(() => {
        if (!hasPoint(matchInfo.value, oddsInfo.value)) return '';

        const isHdpUnderdog = (oddsInfo.value.point < 0 && [OddsOption.Home, OddsOption.Over].includes(priceOption.value.oddsOption))
                || (oddsInfo.value.point >= 0 && [OddsOption.Away, OddsOption.Under].includes(priceOption.value.oddsOption));
        if (isHdpUnderdog) return '';

        const isUnderOption = priceOption.value.oddsOption === OddsOption.Under
                && [MarketType.OverUnder, MarketType.FH_OverUnder].includes(oddsInfo.value.marketType);
        if (isUnderOption) return '';

        return getRealPoint(matchInfo.value, oddsInfo.value, priceOption.value);
    });

    const minimumDecimalPlace = computed(() => {
        if (isCorrectScoreMarket(oddsInfo.value.marketType)) {
            return 0;
        }
        return 2;
    });
    const gaDataMatch = computed(() => {
        if (matchInfo.value?.sportType === SportType.Football) return 'Football';
        if (matchInfo.value?.sportType === SportType.Basketball) return 'Basketball';
        return '';
    });

    let timeoutId: number;

    enum PriceIndicator {
        NoChange = 0,
        Rise = 1,
        Drop = -1,
    }
    const getPriceIndicator = (newPrice: number, oldPrice: number): PriceIndicator => {
        if (oldPrice > 0) {
            if (newPrice > 0) {
                return newPrice > oldPrice ? PriceIndicator.Rise : PriceIndicator.Drop;
            }
            return PriceIndicator.Rise;
        }

        if (oldPrice < 0) {
            if (newPrice > 0) {
                return PriceIndicator.Drop;
            }
            return newPrice > oldPrice ? PriceIndicator.Rise : PriceIndicator.Drop;
        }
        return PriceIndicator.NoChange;
    };
    watch(priceOption, (newVal, oldVal) => {
        if (oddsStatus.isOddsSuspended) {
            oddsStatus.oddRise = false;
            oddsStatus.oddDrop = false;
            return;
        }

        const oldPrice = oldVal?.price ?? 0;
        const newPrice = newVal?.price ?? 0;
        if (oldPrice === newPrice) return;

        const indicator = getPriceIndicator(newPrice, oldPrice);

        if (indicator === PriceIndicator.Rise) {
            oddsStatus.oddRise = true;
            oddsStatus.oddDrop = false;
        } else if (indicator === PriceIndicator.Drop) {
            oddsStatus.oddRise = false;
            oddsStatus.oddDrop = true;
        }

        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            oddsStatus.oddRise = false;
            oddsStatus.oddDrop = false;
        }, 3000);
    }, {
        immediate: true,
    });

    return {
        ...toRefs(oddsStatus),
        point,
        minimumDecimalPlace,
        gaDataMatch,
        toFixedDecimalPoint,
    };
}

export function getRealPoint(matchInfo: IMatch, oddsInfo: IOdds, priceOption: IPriceOption): number | string {
    if (!hasPoint(matchInfo, oddsInfo)) return '';

    if (isFormatPoint(oddsInfo.marketType) && priceOption.oddsOption === OddsOption.Home) {
        return -oddsInfo.point;
    }

    return oddsInfo.point;
}

function hasPoint(matchInfo: IMatch, oddsInfo: IOdds) {
    return isShowPointMarket(oddsInfo.marketType)
    && isShowPointGroup(oddsInfo.marketGroup.id)
    && isShowPointSport(matchInfo.sportType);
}
